import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * Future Self: the bubble is always re-synced from the real cart (/cart.js)
 * after any cart change (theme events, Recharge/subscription adds, or any
 * direct /cart/add|change|update|clear request), so the count never goes stale.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('fs:cart-changed', this.syncFromServer);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
    installCartFetchWatcher();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('fs:cart-changed', this.syncFromServer);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
      this.syncFromServer();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const resourceCount = event.detail?.resource?.item_count;

    if (typeof resourceCount === 'number') {
      this.renderCartBubble(resourceCount, false);
      return;
    }

    // Cart payload not included in the event: ask the server for the truth.
    this.syncFromServer();
  };

  /**
   * Fetches the real cart and renders the bubble from it.
   */
  syncFromServer = async () => {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();
      if (typeof cart.item_count === 'number') {
        this.renderCartBubble(cart.item_count, false);
      }
    } catch (_) {
      // no-op
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the count should be added to the current count.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const previousCount = this.currentCartCount;
    const newCount = comingFromProductForm ? previousCount + itemCount : itemCount;

    this.refs.cartBubbleCount.classList.toggle('hidden', newCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', newCount === 0);

    this.currentCartCount = newCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', newCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || newCount === 0 || newCount === previousCount) return;

    // Ensure element is visible before starting animation
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

/**
 * Watches network calls to the cart endpoints (including those made by apps
 * like Recharge) and tells every cart icon to re-sync when one succeeds.
 */
function installCartFetchWatcher() {
  if (window.__fsCartWatcherInstalled) return;
  window.__fsCartWatcherInstalled = true;

  const cartMutation = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  const originalFetch = window.fetch;
  let timer;

  window.fetch = async function (...args) {
    const response = await originalFetch.apply(this, args);
    try {
      const input = args[0];
      const url = typeof input === 'string' ? input : input?.url || '';
      if (response.ok && cartMutation.test(url)) {
        clearTimeout(timer);
        timer = setTimeout(() => document.dispatchEvent(new CustomEvent('fs:cart-changed')), 150);
      }
    } catch (_) {
      // no-op
    }
    return response;
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
